import isArray from 'lodash/isArray';
import { useHireFormContext } from '../HireForm.context';
import { DropTargetT } from './formElementBuilder/DroppableElement';
import {
  addFollowupParentKey,
  FieldSchema,
  formBuilderType,
  generateFormSchema,
  isFieldSchema,
  mapField,
  MAX_FOLLOW_UP_DEPTH,
  SectionSchema,
} from './formBuilderSchema';
import { v4 as uuidv4 } from 'uuid';
import useHandleAddSignature from './useHandleAddSignature';
import { useToast } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';

export type IDropOnType = 'parent' | 'section' | 'follow-up';
interface UseHandleDropProps {
  // dropOnType: IDropOnType;
  sectionIndex: number;
  fieldIndex?: number;
}

export default function useHandleDrop({ sectionIndex, fieldIndex }: UseHandleDropProps) {
  const { draggedElement, formSchema, setFormSchema, language } = useHireFormContext();
  const toast = useToast();
  const { t } = useTranslation('hrFormTemplate');
  const { checkAddSignature } = useHandleAddSignature();
  const schemas = generateFormSchema(formSchema, language);

  /**
   * Handle the drop event to the section.
   *
   * @param {dropTarget: DropTargetT} - The drop target object.
   * @returns {void}
   */
  function handleDropToSection({ dropTarget }: { dropTarget: DropTargetT['position'] | undefined }): void {
    if (!draggedElement || !formSchema || !formSchema[sectionIndex]) return;
    const { defaultValue: value, type } = draggedElement;
    let defaultValue = checkAddSignature({
      defaultValue: value,
      type,
    });
    defaultValue = addFollowupParentKey(defaultValue);

    const dropType = draggedElement.panel;
    if (dropType === 'page-element') return;

    const isAdvancedSection = Boolean(dropType === 'advanced');
    const newFormSchema = [...formSchema];
    const currentSection = newFormSchema[sectionIndex];

    const insertIndex = dropTarget === 'bottom' ? currentSection.fields.length : fieldIndex ?? 0;

    if (isArray(defaultValue)) {
      if (isAdvancedSection) {
        const newFields = defaultValue?.map((field) =>
          mapField({
            field,
            schemas,
            parentSection: currentSection.section,
            isDrop: true,
          }),
        );
        const newFieldsSchema: SectionSchema = {
          section: draggedElement.section || `section${newFormSchema.length + 1}`,
          isAdvanced: true,
          fields: newFields,
          id: uuidv4(),
        };
        if (!(newFields?.length > 0)) {
          console.error(`Error: The dragged element could not be mapped to a valid field. Field: ${defaultValue}`);
          return;
        }
        currentSection.fields.splice(insertIndex, 0, newFieldsSchema);
      }
    } else {
      const newField = mapField({ field: defaultValue, isDrop: true, schemas });

      if (newField === null) {
        console.error(`Error: The dragged element could not be mapped to a valid field. Field: ${defaultValue}`);
        return;
      }

      currentSection.fields.splice(insertIndex, 0, newField);
    }

    setFormSchema(newFormSchema);
  }

  function handleDropToParent({ dropTarget }: { dropTarget: DropTargetT['position'] | undefined }) {
    if (!draggedElement || !formSchema) return;

    const { panel: dropPanel, type: inputType, defaultValue: value, section: sectionName } = draggedElement;
    let defaultValue = checkAddSignature({
      defaultValue: value,
      type: inputType,
    });
    defaultValue = addFollowupParentKey(defaultValue);
    if (defaultValue === null) {
      console.error(`Error: The dragged element could not be mapped to a valid field. Field: ${defaultValue}`);
      return;
    }

    const isLastSection = sectionIndex === formSchema.length - 1;
    const insertIndex = isLastSection ? sectionIndex + 1 : dropTarget === 'bottom' ? sectionIndex + 1 : sectionIndex;

    const newFormSchema = [...formSchema];
    const newSection: SectionSchema = { section: 'section' + (formSchema.length + 1), fields: [], id: uuidv4() };

    const hasSignature = formSchema
      .flatMap((section) => section.fields)
      .some((field) => isFieldSchema(field) && field.inputType === formBuilderType.signatureAdvanced);

    // Insert the NEW SECTION
    if (dropPanel === 'page-element') {
      if (hasSignature) {
        newFormSchema.splice(newFormSchema.length - 1, 0, newSection);
      } else {
        newFormSchema.splice(insertIndex, 0, newSection);
      }
      setFormSchema(newFormSchema);
      return;
    }

    // Insert the new ADVANCED SECTION
    if (dropPanel === 'advanced' && isArray(defaultValue)) {
      if (sectionName) {
        newSection.section = sectionName;
      }
      newSection.isAdvanced = true;
      newSection.fields = defaultValue.map((field) => mapField({ field, isDrop: true, schemas }));
      if (newSection.fields === null) {
        console.error(`Error: The dragged element could not be mapped to a valid field. Field: ${defaultValue}`);
        return;
      }

      if (hasSignature) {
        newFormSchema.splice(newFormSchema.length - 1, 0, newSection);
      } else if (
        inputType === formBuilderType.signatureAdvanced ||
        (newFormSchema.length === 1 && newFormSchema[0].fields.length === 0)
      ) {
        newFormSchema.push(newSection);
      } else {
        newFormSchema.splice(insertIndex, 0, newSection);
      }
      setFormSchema(newFormSchema);
      return;
    }

    // Insert the new BASIC ELEMENT
    newSection.fields = [mapField({ field: defaultValue as FieldSchema, isDrop: true, schemas })];
    if (newSection.fields === null) {
      console.error(`Error: The dragged element could not be mapped to a valid field. Field: ${defaultValue}`);
      return;
    }

    if (hasSignature) {
      newFormSchema.splice(newFormSchema.length - 1, 0, newSection);
    } else {
      newFormSchema.splice(insertIndex, 0, newSection);
    }
    setFormSchema(newFormSchema);
  }

  function handleDropFollowUp({
    dropTarget,
    followDepth,
    parent,
    index,
    optionId,
  }: {
    dropTarget: DropTargetT['position'] | undefined;
    followDepth: number;
    parent: FieldSchema;
    index: number;
    optionId: string;
  }) {
    if (!draggedElement || !formSchema) return;

    const { panel: dropPanel, type: inputType, defaultValue: value } = draggedElement;

    if (followDepth >= MAX_FOLLOW_UP_DEPTH && inputType === formBuilderType.yesNo) {
      toast({
        description: t('formBuilder.followups.errors.maxDepth', { MAX_FOLLOW_UP_DEPTH }),
        status: 'error',
        isClosable: true,
      });
      return;
    }
    if (inputType === formBuilderType.documentReview) {
      toast({
        description: t('formBuilder.followups.errors.noDocumentReview'),
        status: 'error',
        isClosable: true,
      });
      return;
    }
    if (dropPanel === 'advanced' && isArray(value)) {
      toast({
        description: t('formBuilder.followups.errors.noAdvancedComponent'),
        status: 'error',
        isClosable: true,
      });
      return;
    }
    if (dropPanel === 'page-element') {
      toast({
        description: t('formBuilder.followups.errors.noSection'),
        status: 'error',
        isClosable: true,
      });
      return;
    }

    const followUpQuestions = [...(parent?.followUpQuestionList ?? [])];

    let spliceIndex = index;
    if (index === followUpQuestions.length - 1 && dropTarget === 'bottom') {
      spliceIndex = index + 1;
    } else {
      if (dropTarget === 'top') {
        spliceIndex = index;
      } else {
        spliceIndex = index;
      }
    }

    let defaultValue = checkAddSignature({
      defaultValue: value,
      type: inputType,
    });

    if (isArray(defaultValue)) {
      return;
    }
    defaultValue.parent = parent.id;

    followUpQuestions?.splice(spliceIndex, 0, {
      ...defaultValue,
      id: uuidv4(),
      parent: parent.id,
      optionId,
      timestamp: Date.now(),
      isFollowUp: true,
    });

    return followUpQuestions;
  }

  return { handleDropToSection, handleDropToParent, handleDropFollowUp };
}
